import { BATTLE_PASS_TIERS } from '@/models/battle-pass';
import type { BattlePassManager } from '@/systems/BattlePassManager';

function rewardLabel(reward: { type: string; value: number | string }): string {
  if (reward.type === 'gems') return `💎 ${reward.value}`;
  if (reward.type === 'resources') return `⚡ ${reward.value}`;
  return `🎨 ${reward.value}`;
}

export class BattlePassPanel {
  private container: HTMLElement;
  private xpFill: HTMLElement;
  private xpText: HTMLElement;
  private tierCountEl: HTMLElement;
  private tierRows: HTMLElement[] = [];
  private claimButtons: HTMLButtonElement[] = [];
  private premiumClaimButtons: HTMLButtonElement[] = [];
  private upgradeBtn: HTMLButtonElement;
  private onUpgrade?: () => void;
  private onClaimFree?: (tier: number) => void;
  private onClaimPremium?: (tier: number) => void;

  constructor(
    parent: HTMLElement,
    onUpgrade?: () => void,
    onClaimFree?: (tier: number) => void,
    onClaimPremium?: (tier: number) => void,
  ) {
    this.onUpgrade = onUpgrade;
    this.onClaimFree = onClaimFree;
    this.onClaimPremium = onClaimPremium;

    this.container = document.createElement('div');
    this.container.id = 'battle-pass-panel';
    this.container.className = 'game-panel';
    this.container.style.borderColor = 'rgba(168, 85, 247, 0.5)';
    this.container.style.width = '320px';

    // Header
    const header = document.createElement('div');
    header.className = 'game-panel-header';
    header.innerHTML = `
      <div class="game-panel-title" style="color: var(--purple);">⚔️ BATTLE PASS</div>
      <button class="game-panel-close">&times;</button>
    `;
    header.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    this.container.appendChild(header);

    // XP bar section
    const xpSection = document.createElement('div');
    xpSection.style.marginBottom = '8px';

    this.tierCountEl = document.createElement('div');
    this.tierCountEl.style.cssText = 'font-size: 11px; color: var(--text-dim); text-align: center; margin-bottom: 4px;';
    this.tierCountEl.textContent = 'Tier 0 / 30';

    const xpBarWrapper = document.createElement('div');
    xpBarWrapper.className = 'bp-xp-bar';
    this.xpFill = document.createElement('div');
    this.xpFill.className = 'bp-xp-fill';
    this.xpFill.style.width = '0%';
    xpBarWrapper.appendChild(this.xpFill);

    this.xpText = document.createElement('div');
    this.xpText.style.cssText = 'font-size: 10px; color: var(--text-dim); text-align: center; margin-top: 2px;';
    this.xpText.textContent = '0 / 100 XP';

    xpSection.appendChild(this.tierCountEl);
    xpSection.appendChild(xpBarWrapper);
    xpSection.appendChild(this.xpText);
    this.container.appendChild(xpSection);

    // Upgrade button (shown when not premium)
    this.upgradeBtn = document.createElement('button');
    this.upgradeBtn.className = 'btn';
    this.upgradeBtn.style.cssText = `
      width: 100%; margin-bottom: 10px; padding: 8px;
      background: linear-gradient(135deg, rgba(168,85,247,0.3), rgba(91,155,213,0.2));
      border: 1px solid rgba(168,85,247,0.5); color: var(--purple);
      font-size: 12px; font-weight: 700; border-radius: var(--radius-sm);
      cursor: pointer;
    `;
    this.upgradeBtn.textContent = 'Upgrade to Premium — $4.99';
    this.upgradeBtn.addEventListener('click', () => this.onUpgrade?.());
    this.container.appendChild(this.upgradeBtn);

    // Tier list container
    const listWrapper = document.createElement('div');
    listWrapper.style.cssText = 'max-height: 45vh; overflow-y: auto;';

    // Build tier rows
    for (const tierDef of BATTLE_PASS_TIERS) {
      const row = document.createElement('div');
      row.className = 'bp-tier locked';

      // Tier number circle
      const numEl = document.createElement('div');
      numEl.className = 'bp-tier-num';
      numEl.textContent = String(tierDef.tier);

      // Free reward
      const freeEl = document.createElement('div');
      freeEl.style.cssText = 'flex: 1; font-size: 11px; color: var(--text);';
      freeEl.textContent = rewardLabel(tierDef.freeReward);

      // Free claim button
      const claimBtn = document.createElement('button');
      claimBtn.className = 'btn btn-upgrade';
      claimBtn.style.cssText = 'font-size: 10px; padding: 4px 8px; min-width: 48px;';
      claimBtn.textContent = 'Claim';
      claimBtn.disabled = true;
      claimBtn.addEventListener('click', () => this.onClaimFree?.(tierDef.tier));
      this.claimButtons.push(claimBtn);

      row.appendChild(numEl);
      row.appendChild(freeEl);
      row.appendChild(claimBtn);

      // Premium reward column
      const premiumEl = document.createElement('div');
      premiumEl.style.cssText = 'font-size: 10px; color: var(--purple); min-width: 60px; text-align: right;';

      if (tierDef.premiumReward) {
        premiumEl.textContent = `★ ${tierDef.premiumReward.value}`;
        const premClaimBtn = document.createElement('button');
        premClaimBtn.className = 'btn';
        premClaimBtn.style.cssText = 'font-size: 9px; padding: 3px 6px; background: rgba(168,85,247,0.15); border: 1px solid rgba(168,85,247,0.3); color: var(--purple); margin-left: 4px;';
        premClaimBtn.textContent = 'Claim';
        premClaimBtn.disabled = true;
        premClaimBtn.addEventListener('click', () => this.onClaimPremium?.(tierDef.tier));
        this.premiumClaimButtons.push(premClaimBtn);
        row.appendChild(premiumEl);
        row.appendChild(premClaimBtn);
      } else {
        premiumEl.textContent = '';
        this.premiumClaimButtons.push(document.createElement('button')); // placeholder
        row.appendChild(premiumEl);
      }

      this.tierRows.push(row);
      listWrapper.appendChild(row);
    }

    this.container.appendChild(listWrapper);
    parent.appendChild(this.container);
  }

  update(manager: BattlePassManager): void {
    const currentTier = manager.getCurrentTier();
    const currentXP = manager.getCurrentXP();
    const xpForNext = manager.getXPForNextTier();
    const isPremium = manager.isPremium();

    // Update tier count
    this.tierCountEl.textContent = `Tier ${currentTier} / ${BATTLE_PASS_TIERS.length}`;

    // Update XP bar
    if (currentTier >= BATTLE_PASS_TIERS.length) {
      this.xpFill.style.width = '100%';
      this.xpText.textContent = 'MAX TIER';
    } else {
      const prevXP = currentTier > 0 ? BATTLE_PASS_TIERS[currentTier - 1]!.xpRequired : 0;
      const span = xpForNext - prevXP;
      const progress = span > 0 ? ((currentXP - prevXP) / span) * 100 : 0;
      this.xpFill.style.width = `${Math.min(100, Math.max(0, progress)).toFixed(1)}%`;
      this.xpText.textContent = `${currentXP} / ${xpForNext} XP`;
    }

    // Show/hide upgrade button
    this.upgradeBtn.style.display = isPremium ? 'none' : 'block';

    // Update each tier row
    for (let i = 0; i < BATTLE_PASS_TIERS.length; i++) {
      const tierNum = i + 1;
      const row = this.tierRows[i]!;
      const claimBtn = this.claimButtons[i]!;
      const premClaimBtn = this.premiumClaimButtons[i]!;
      const isUnlocked = currentTier >= tierNum;
      const isFreeClaimed = manager.isFreeClaimed(tierNum);

      // Row state classes
      row.classList.remove('locked', 'unlocked', 'claimed');
      if (!isUnlocked) {
        row.classList.add('locked');
      } else if (isFreeClaimed) {
        row.classList.add('claimed');
      } else {
        row.classList.add('unlocked');
      }

      // Free claim button
      claimBtn.disabled = !isUnlocked || isFreeClaimed;
      claimBtn.textContent = isFreeClaimed ? 'Claimed' : 'Claim';

      // Premium claim button
      if (premClaimBtn.tagName === 'BUTTON' && premClaimBtn.style.cssText !== '') {
        const isPremClaimed = manager.isPremiumClaimed(tierNum);
        premClaimBtn.disabled = !isPremium || !isUnlocked || isPremClaimed;
        premClaimBtn.textContent = isPremClaimed ? 'Claimed' : !isPremium ? 'Premium' : 'Claim';
      }
    }
  }

  show(): void { this.container.style.display = 'block'; }
  hide(): void { this.container.style.display = 'none'; }
  toggle(): void {
    this.container.style.display === 'none' ? this.show() : this.hide();
  }
  destroy(): void { this.container.remove(); }
}
